import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { termsAPI } from '../../services/termsAPI';

interface TermsState {
  terms: any[];
  totalTerms: number;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: TermsState = {
  terms: [],
  totalTerms: 0,
  currentPage: 1,
  totalPages: 0,
  isLoading: false,
  error: null,
};

export const fetchTerms = createAsyncThunk(
  'terms/fetchTerms',
  async (params: { page?: number; limit?: number; type?: string }) => {
    return await termsAPI.getAllTerms(params);
  }
);

export const createTerm = createAsyncThunk(
  'terms/createTerm',
  async (data: { type: 'Privacy-Policy' | 'Refund-Policy'; title: string; description: string }) => {
    return await termsAPI.createTerm(data);
  }
);

export const updateTerm = createAsyncThunk(
  'terms/updateTerm',
  async ({ id, data }: { id: string; data: { title?: string; description?: string } }) => {
    return await termsAPI.updateTerm(id, data);
  }
);

export const deleteTerm = createAsyncThunk(
  'terms/deleteTerm',
  async (id: string) => {
    await termsAPI.deleteTerm(id);
    return id;
  }
);

const termsSlice = createSlice({
  name: 'terms',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTerms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTerms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.terms = action.payload.data || [];
        state.totalTerms = action.payload.total || 0;
        state.currentPage = action.payload.page || 1;
        state.totalPages = action.payload.totalPages || 0;
      })
      .addCase(fetchTerms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch terms';
      })
      .addCase(createTerm.fulfilled, (state, action) => {
        state.terms.unshift(action.payload.data);
      })
      .addCase(updateTerm.fulfilled, (state, action) => {
        const index = state.terms.findIndex(term => term._id === action.payload.data._id);
        if (index !== -1) {
          state.terms[index] = action.payload.data;
        }
      })
      .addCase(deleteTerm.fulfilled, (state, action) => {
        state.terms = state.terms.filter(term => term._id !== action.payload);
      });
  },
});

export const { clearError } = termsSlice.actions;
export default termsSlice.reducer;